import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Plus, Webhook as WebhookIcon, Save, Trash2, Loader2, Send, Copy, RefreshCw, KeyRound,
  CheckCircle2, XCircle, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/misc';
import { apiFetch } from '@/lib/api';
import { ALL_EVENT_TYPES, eventGroupsFor, eventLabel } from '@/lib/webhook-events';
import { useProjects } from '@/components/ProjectSwitcher';
import { getProject } from '@/lib/api';
import { isOfficial } from '@/lib/projects';
import { useT } from '@/lib/i18n';

interface Webhook { id: string; url: string; event_types: string[]; number_filter: string; active: boolean; created_at?: string }
interface Delivery { id: string; event_type: string; status: string; attempts: number; last_error?: string; updated_at: string }
interface Instance { id: string; phone: string; nickname?: string }

const DRAFT: Webhook = { id: '', url: '', event_types: [], number_filter: '', active: true };

export function Webhooks() {
  const t = useT();
  const qc = useQueryClient();
  const [selId, setSelId] = useState<string | null>(null);
  const [draft, setDraft] = useState<Webhook | null>(null); // novo webhook não salvo

  const { data: hooks = [], isLoading } = useQuery({
    queryKey: ['webhooks'],
    queryFn: async (): Promise<Webhook[]> => ((await apiFetch('GET', '/webhooks')).data as { data?: Webhook[] })?.data ?? [],
  });
  const { data: instances = [] } = useQuery({
    queryKey: ['instances'],
    queryFn: async (): Promise<Instance[]> => ((await apiFetch('GET', '/instances')).data as { data?: Instance[] })?.data ?? [],
  });

  const selected = draft ?? hooks.find((h) => h.id === selId) ?? null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['webhooks'] });

  const newHook = () => { setDraft({ ...DRAFT }); setSelId(null); };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">Receba os eventos do seu projeto (mensagens, status, conexão) na sua URL — com assinatura HMAC e retry. Cadastre quantos quiser e escolha o que cada um escuta.</p>
        <Button className="w-full shrink-0 sm:w-auto" onClick={newHook}><Plus className="size-4" /> Novo webhook</Button>
      </div>

      <div className="grid gap-6 md:grid-cols-[20rem_1fr]">
        {/* Lista */}
        <Card className="h-fit">
          <CardContent className="flex flex-col gap-1 p-2">
            {isLoading ? (
              [0, 1].map((i) => <Skeleton key={i} className="h-12 w-full" />)
            ) : hooks.length === 0 && !draft ? (
              <div className="flex flex-col items-center gap-2 py-10 text-center">
                <WebhookIcon className="size-7 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">Nenhum webhook ainda.</p>
                <Button size="sm" onClick={newHook}><Plus className="size-4" /> Criar o primeiro</Button>
              </div>
            ) : (
              <>
                {draft && (
                  <div className="flex items-center gap-2 rounded-md bg-accent px-2 py-2 text-sm">
                    <span className="size-2 rounded-full bg-amber-400" />
                    <span className="flex-1 truncate text-muted-foreground italic">Novo webhook…</span>
                  </div>
                )}
                {hooks.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => { setDraft(null); setSelId(h.id); }}
                    className={'flex items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors ' + (!draft && h.id === selId ? 'bg-accent' : 'hover:bg-accent/50')}
                  >
                    <span className={'size-2 shrink-0 rounded-full ' + (h.active ? 'bg-emerald-500' : 'bg-muted-foreground/40')} />
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">{prettyUrl(h.url)}</p>
                      <p className="truncate text-xs text-muted-foreground">{h.event_types.length === 0 ? 'Todos os eventos' : `${h.event_types.length} evento(s)`}</p>
                    </div>
                  </button>
                ))}
              </>
            )}
          </CardContent>
        </Card>

        {/* Editor */}
        {selected ? (
          <WebhookEditor
            key={selected.id || 'draft'}
            hook={selected}
            allHooks={hooks}
            instances={instances}
            onSaved={(id) => { setDraft(null); setSelId(id); refresh(); }}
            onDeleted={() => { setDraft(null); setSelId(null); refresh(); }}
            onCancelDraft={() => setDraft(null)}
          />
        ) : (
          <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">Selecione um webhook ou crie um novo.</CardContent></Card>
        )}
      </div>

      <RelayHelp />
      <span className="sr-only">{t('nav.webhooks')}</span>
    </div>
  );
}

function RelayHelp() {
  const [copied, setCopied] = useState(false);
  const cmd = 'curl -X POST https://api.bzapper.com.br/webhooks/trigger -H "Authorization: Bearer SUA_API_KEY" -H "Content-Type: application/json" -d \'{"event_type":"message.received"}\'';
  return (
    <Card className="border-primary/30">
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center gap-2">
          <WebhookIcon className="size-4 text-primary" />
          <h2 className="text-base font-semibold">Testar a sua integração</h2>
        </div>
        <p className="text-sm text-muted-foreground">
          Use <strong>Enviar teste</strong> no webhook acima (ou o <strong>Playground</strong>) para disparar
          um evento de exemplo no seu endpoint, com <code>X-Bzapper-Signature</code> real. Para ver os
          eventos ao vivo sem expor URL nenhuma, abra o stream <code>GET /stream</code>.
        </p>
        <div className="flex items-center gap-2">
          <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs">{cmd}</code>
          <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(cmd); setCopied(true); setTimeout(() => setCopied(false), 1500); }}>
            {copied ? <CheckCircle2 className="size-4 text-emerald-600" /> : <Copy className="size-4" />}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Valide sempre a assinatura sobre o corpo cru recebido, antes de dar parse no JSON.
        </p>
      </CardContent>
    </Card>
  );
}

function WebhookEditor({
  hook, allHooks, instances, onSaved, onDeleted, onCancelDraft,
}: {
  hook: Webhook;
  allHooks: Webhook[];
  instances: Instance[];
  onSaved: (id: string) => void;
  onDeleted: () => void;
  onCancelDraft: () => void;
}) {
  const isNew = !hook.id;

  // Unicidade: cada evento só pode pertencer a UM webhook. Calcula o que já foi
  // tomado por OUTROS webhooks (e se algum outro escuta "todos").
  const others = allHooks.filter((h) => h.id !== hook.id);
  const otherAll = others.some((h) => h.event_types.length === 0);
  const takenBy = new Map<string, string>();
  for (const h of others) for (const e of h.event_types) takenBy.set(e, h.url);
  const isTaken = (type: string) => otherAll || takenBy.has(type);
  const [url, setUrl] = useState(hook.url);
  const [events, setEvents] = useState<string[]>(hook.event_types);
  const [allEvents, setAllEvents] = useState(hook.event_types.length === 0);
  const [numberFilter, setNumberFilter] = useState(hook.number_filter);
  const [active, setActive] = useState(hook.active);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [secret, setSecret] = useState<string | null>(null); // exibido uma vez
  const [showSecret, setShowSecret] = useState(true);
  const { data: projects = [] } = useProjects();
  const project = projects.find((p) => p.id === getProject()) ?? projects.find((p) => p.is_default);
  const groups = eventGroupsFor(isOfficial(project));

  const body = () => ({ url: url.trim(), event_types: allEvents ? [] : events, number_filter: numberFilter, active });

  const save = async () => {
    if (!/^https?:\/\//i.test(url.trim())) { toast.error('Informe uma URL http(s) válida.'); return; }
    setSaving(true);
    try {
      const r = isNew
        ? await apiFetch('POST', '/webhooks', body())
        : await apiFetch('PATCH', `/webhooks/${hook.id}`, body());
      if (!r.ok) {
        const msg = (r.data as { error?: string; code?: string })?.error;
        toast.error(r.status === 409 ? (msg ?? 'Esse evento já tem um webhook.') : 'Falha ao salvar o webhook.');
        return;
      }
      const data = r.data as Webhook & { secret?: string };
      if (data.secret) { setSecret(data.secret); setShowSecret(true); }
      toast.success(isNew ? 'Webhook criado.' : 'Webhook salvo.');
      onSaved(data.id);
    } finally { setSaving(false); }
  };

  const regenerate = async () => {
    if (!window.confirm('Gerar um novo segredo? O atual deixará de validar as assinaturas.')) return;
    const r = await apiFetch('PATCH', `/webhooks/${hook.id}`, { ...body(), secret: 'regenerate' });
    if (!r.ok) { toast.error('Falha ao regenerar o segredo.'); return; }
    setSecret((r.data as { secret?: string }).secret ?? null);
    setShowSecret(true);
    toast.success('Novo segredo gerado.');
  };

  const del = async () => {
    if (!window.confirm('Excluir este webhook?')) return;
    setDeleting(true);
    try {
      const r = await apiFetch('DELETE', `/webhooks/${hook.id}`);
      if (r.ok) { toast.success('Webhook excluído.'); onDeleted(); }
      else toast.error('Falha ao excluir.');
    } finally { setDeleting(false); }
  };

  const toggleEvent = (type: string) =>
    setEvents((e) => (e.includes(type) ? e.filter((x) => x !== type) : [...e, type]));

  return (
    <div className="flex flex-col gap-6">
      {/* Secret recém-gerado */}
      {secret && (
        <Card className="border-emerald-400/40 bg-emerald-50/50 dark:bg-emerald-500/5">
          <CardContent className="flex flex-col gap-2 p-5">
            <div className="flex items-center gap-2 text-sm font-semibold"><KeyRound className="size-4 text-emerald-600" /> Segredo de assinatura</div>
            <p className="text-xs text-muted-foreground">Guarde agora — por segurança, ele <strong>não será exibido novamente</strong>. Use-o para validar o header <code>X-Bzapper-Signature</code>.</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md border bg-card px-3 py-2 font-mono text-sm">{showSecret ? secret : '•'.repeat(32)}</code>
              <Button type="button" variant="outline" size="sm" onClick={() => setShowSecret((s) => !s)}>{showSecret ? <EyeOff className="size-4" /> : <Eye className="size-4" />}</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(secret); toast.success('Segredo copiado.'); }}><Copy className="size-4" /></Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Detalhes */}
      <Card>
        <CardContent className="flex flex-col gap-5 p-5">
          <div>
            <h2 className="text-base font-semibold">{isNew ? 'Novo webhook' : 'Webhook'}</h2>
            <p className="text-sm text-muted-foreground">Para onde e o que enviar.</p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>URL de destino</Label>
            <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://seu-app.com/webhooks/bzapper" className="font-mono" />
          </div>

          {/* Eventos — cada evento só pode pertencer a UM webhook. */}
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
              <Label>Eventos a escutar</Label>
              <label className={'flex items-center gap-2 text-sm ' + (others.length > 0 ? 'cursor-not-allowed text-muted-foreground/50' : 'cursor-pointer text-muted-foreground')} title={others.length > 0 ? 'Já existem outros webhooks — "todos" causaria conflito.' : ''}>
                <input type="checkbox" checked={allEvents} disabled={others.length > 0} onChange={(e) => setAllEvents(e.target.checked)} className="size-4 accent-primary" />
                Todos os eventos
              </label>
            </div>
            <p className="text-xs text-muted-foreground">Cada evento pertence a <strong>um único webhook</strong> — os já usados por outro aparecem desabilitados.</p>
            {!allEvents && (
              <div className="grid gap-4 sm:grid-cols-2">
                {groups.map((g) => (
                  <div key={g.group} className="flex flex-col gap-1.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.group}</p>
                    {g.events.map((ev) => {
                      const taken = isTaken(ev.type) && !events.includes(ev.type);
                      return (
                        <label key={ev.type} className={'flex items-start gap-2 rounded-md px-1 py-1 ' + (taken ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:bg-accent/50')} title={taken ? 'Já usado por outro webhook' : ev.hint}>
                          <input type="checkbox" checked={events.includes(ev.type)} disabled={taken} onChange={() => toggleEvent(ev.type)} className="mt-0.5 size-4 accent-primary" />
                          <span className="text-sm">{ev.label} <code className="text-[11px] text-muted-foreground">{ev.type}</code></span>
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
            {allEvents && <p className="text-xs text-muted-foreground">Recebe todos os tipos de evento. Desmarque para escolher um subconjunto.</p>}
          </div>

          {/* Filtro de número + ativo */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5">
              <Label>Número (opcional)</Label>
              <select value={numberFilter} onChange={(e) => setNumberFilter(e.target.value)} className="h-9 rounded-md border bg-transparent px-3 text-sm">
                <option value="">Todos os números do projeto</option>
                {instances.map((i) => <option key={i.id} value={i.id}>{i.nickname || i.phone}</option>)}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>Status</Label>
              <label className="flex h-9 cursor-pointer items-center gap-2 text-sm">
                <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} className="size-4 accent-primary" />
                {active ? 'Ativo (recebendo eventos)' : 'Pausado'}
              </label>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t pt-4">
            <div className="flex gap-2">
              {!isNew && <Button variant="outline" size="sm" onClick={regenerate}><RefreshCw className="size-4" /> Regenerar segredo</Button>}
              {isNew && <Button variant="ghost" size="sm" onClick={onCancelDraft}>Cancelar</Button>}
            </div>
            <div className="flex gap-2">
              {!isNew && <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" disabled={deleting} onClick={del}>{deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />} Excluir</Button>}
              <Button disabled={saving} onClick={save}>{saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} {isNew ? 'Criar' : 'Salvar'}</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Teste + entregas (só para webhooks salvos) */}
      {!isNew && <TestPanel hook={hook} allEvents={allEvents} events={events} />}
      {!isNew && <DeliveriesPanel webhookId={hook.id} />}
      {!isNew && <SignatureHelp />}
    </div>
  );
}

function TestPanel({ hook, allEvents, events }: { hook: Webhook; allEvents: boolean; events: string[] }) {
  const options = allEvents ? ALL_EVENT_TYPES : (events.length ? events : ALL_EVENT_TYPES);
  const [evType, setEvType] = useState(options[0] ?? 'message.received');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ status: number; delivered: boolean; error?: string } | null>(null);

  const test = async () => {
    setSending(true); setResult(null);
    try {
      const r = await apiFetch('POST', `/webhooks/${hook.id}/test`, { event_type: evType });
      setResult(r.data as { status: number; delivered: boolean; error?: string });
    } finally { setSending(false); }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div>
          <h2 className="text-base font-semibold">Enviar evento de teste</h2>
          <p className="text-sm text-muted-foreground">Dispara um payload de exemplo para a URL e mostra a resposta.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={evType} onChange={(e) => setEvType(e.target.value)} className="h-9 rounded-md border bg-transparent px-3 text-sm">
            {options.map((o) => <option key={o} value={o}>{eventLabel(o)} ({o})</option>)}
          </select>
          <Button disabled={sending} onClick={test}>{sending ? <Loader2 className="size-4 animate-spin" /> : <Send className="size-4" />} Enviar teste</Button>
          {result && (
            <span className={'inline-flex items-center gap-1.5 text-sm font-medium ' + (result.delivered ? 'text-emerald-600' : 'text-rose-600')}>
              {result.delivered ? <CheckCircle2 className="size-4" /> : <XCircle className="size-4" />}
              {result.error ? result.error : `HTTP ${result.status}`}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function DeliveriesPanel({ webhookId }: { webhookId: string }) {
  const { data: list = [], refetch, isFetching } = useQuery({
    queryKey: ['webhook-deliveries', webhookId],
    queryFn: async (): Promise<Delivery[]> => ((await apiFetch('GET', `/webhooks/${webhookId}/deliveries?limit=50`)).data as { data?: Delivery[] })?.data ?? [],
    refetchInterval: 5000,
  });
  const [redelivering, setRedelivering] = useState<string | null>(null);

  // Reenvio de entrega que falhou: mesmo corpo e mesmo event_id, assinado com o
  // segredo atual. É o "tentar de novo" de quem acabou de consertar o endpoint.
  const redeliver = async (d: Delivery) => {
    setRedelivering(d.id);
    try {
      const r = await apiFetch('POST', `/webhooks/${webhookId}/deliveries/${d.id}/redeliver`);
      const out = r.data as { delivered?: boolean; status?: number; error?: string; message?: string } | null;
      if (!r.ok) toast.error(out?.message ?? 'Não foi possível reenviar.');
      else if (out?.delivered) toast.success(`Reenviado: seu endpoint respondeu ${out.status}.`);
      else toast.error(`Reenvio falhou${out?.status ? ` (HTTP ${out.status})` : ''}: ${out?.error ?? 'sem resposta'}`);
      void refetch();
    } finally {
      setRedelivering(null);
    }
  };

  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Entregas recentes</h2>
            <p className="text-sm text-muted-foreground">Últimas tentativas (atualiza sozinho).</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => refetch()}><RefreshCw className={'size-4 ' + (isFetching ? 'animate-spin' : '')} /></Button>
        </div>
        {list.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma entrega ainda. Use "Enviar evento de teste" acima.</p>
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full min-w-md text-sm">
              <thead><tr className="border-b bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="px-3 py-2 font-medium">Evento</th><th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Tent.</th><th className="px-3 py-2 font-medium">Quando</th>
                <th className="px-3 py-2" />
              </tr></thead>
              <tbody>
                {list.map((d) => (
                  <tr key={d.id} className="border-b last:border-0">
                    <td className="px-3 py-2 font-mono text-xs">{d.event_type}</td>
                    <td className="px-3 py-2">
                      <Badge variant={d.status === 'delivered' ? 'success' : d.status === 'failed' ? 'destructive' : 'secondary'}>{d.status}</Badge>
                      {d.last_error && <span className="ml-2 text-xs text-muted-foreground" title={d.last_error}>{d.last_error.slice(0, 40)}</span>}
                    </td>
                    <td className="px-3 py-2 tabular-nums">{d.attempts}</td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(d.updated_at).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right">
                      {d.status === 'failed' && (
                        <Button size="sm" variant="outline" disabled={redelivering === d.id} onClick={() => void redeliver(d)}
                          title="Reenvia o mesmo evento (só falhas dos últimos 7 dias)">
                          {redelivering === d.id ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />} Reenviar
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const SNIPPET = `// Node/Express — valide a assinatura (HMAC-SHA256 do corpo cru)
import crypto from 'node:crypto';

app.post('/webhooks/bzapper', express.raw({ type: '*/*' }), (req, res) => {
  const sig = req.header('X-Bzapper-Signature');
  const mac = crypto.createHmac('sha256', process.env.BZAPPER_WEBHOOK_SECRET)
    .update(req.body).digest('hex');
  if (sig !== mac) return res.status(401).end();        // assinatura inválida
  const event = JSON.parse(req.body);                   // { event_type, payload, ... }
  res.status(200).end();                                // 2xx = entregue
});`;

function SignatureHelp() {
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-5">
        <div>
          <h2 className="text-base font-semibold">Verificar a assinatura</h2>
          <p className="text-sm text-muted-foreground">Cada requisição traz <code>X-Bzapper-Signature</code> = HMAC-SHA256 do corpo cru com o seu segredo. Responda <strong>2xx</strong> para confirmar a entrega; reentregamos com retry/backoff em caso de erro.</p>
        </div>
        <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed"><code>{SNIPPET}</code></pre>
      </CardContent>
    </Card>
  );
}

function prettyUrl(u: string): string {
  try { const p = new URL(u); return p.host + (p.pathname !== '/' ? p.pathname : ''); } catch { return u || '—'; }
}
